import * as Sentry from "@sentry/react";
import type { ApiErrorResult } from "_shared/_errors/api-error";

const reportedApiOperations = new Set<string>();
const reportedSchemaOperations = new Set<string>();

/** Reports only bounded contract metadata. Request/response values and error text are never sent. */
export function reportApiError(error: ApiErrorResult, operation: string): void {
  if (error.category !== "unexpected" || error.status !== 400 || !error.traceId) return;

  const key = `${error.traceId}:${operation}`;
  if (reportedApiOperations.has(key)) return;
  reportedApiOperations.add(key);

  captureSanitizedMessage("Unexpected API 400", {
    tags: {
      operation,
      status: String(error.status),
      code: error.code ?? "unknown",
      traceId: error.traceId,
    },
  });
}

export type SchemaFailureReport = {
  schemaName: string;
  operation: string;
  phase: string;
  fields: readonly string[];
};

export function reportSchemaFailure(report: SchemaFailureReport): void {
  const fields = [...new Set(report.fields)].sort().join(",") || "unknown";
  const key = `${report.schemaName}:${report.operation}:${report.phase}:${fields}`;
  if (reportedSchemaOperations.has(key)) return;
  reportedSchemaOperations.add(key);

  captureSanitizedMessage("Client schema contract mismatch", {
    tags: {
      schema: report.schemaName,
      operation: report.operation,
      phase: report.phase,
    },
    extra: { fields },
  });
}

export function resetClientErrorReports(): void {
  reportedApiOperations.clear();
  reportedSchemaOperations.clear();
}

type SanitizedTelemetry = {
  tags: Record<string, string>;
  extra?: Record<string, string>;
};

/**
 * These reports can be raised after a failed fetch, when the active Sentry scope can contain request
 * breadcrumbs and unrelated page context. Replace the fully prepared event, after all SDK processors
 * have run, so only this module's bounded metadata reaches the transport.
 */
function captureSanitizedMessage(message: string, telemetry: SanitizedTelemetry): void {
  Sentry.withScope((scope) => {
    scope.addEventProcessor((event) => ({
      event_id: event.event_id,
      timestamp: event.timestamp,
      message,
      level: "warning",
      tags: telemetry.tags,
      ...(telemetry.extra ? { extra: telemetry.extra } : {}),
    }));
    Sentry.captureMessage(message, { level: "warning", ...telemetry });
  });
}
